import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { showErrorToast, showSuccessToast } from 'components/Toast';
import InternalHeader from 'components/Navigation/InternalHeader';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import OutlineButton from 'components/OutlineButton';
import BaseInput from 'components/Inputs/BaseInput';
import SolidButton from 'components/SolidButton';

import { REQUIRED_RULE } from 'utils/constants';
import { BUTTON_COLORS } from 'utils/enums';
import StorageService from 'utils/storage';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';

export default function ClaimPage() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      document_type: StorageService.get('userData')?.document_type,
      number: StorageService.get('userData')?.number,
      name: StorageService.get('userData')?.full_name,
      email: StorageService.get('userData')?.email,
    },
  });
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);

  const submitAction = async (data: any) => {
    setLoading(true);
    let result: any = await generalApi.post('/add_claim', data);
    setLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('Reclamo creado');
    setTimeout(function () {
      history('/my_operations');
    }, 5000);
  };

  const buildBaseInput = (
    label: string,
    type: React.HTMLInputTypeAttribute,
    name: string,
    rules: any,
    props: any
  ) => {
    return (
      <BaseInput
        label={label}
        type={type}
        name={name}
        register={register}
        errors={errors}
        rules={rules}
        {...props}
      />
    );
  };
  return (
    <PrivateRoute>
      <div className="min-h-full bg-gray-100 text-[#1B205A]">
        <InternalHeader />
        <div className="mx-auto max-w-5xl pb-8 pt-32 sm:pt-0 2xl:px-24">
          <div className="justify-between space-y-4 px-8 pt-8 pb-4 sm:flex sm:space-y-0 sm:px-0">
            <p className="text-2xl font-bold">Libro de Reclamaciones</p>
            <OutlineButton color={BUTTON_COLORS.BLUE} onClick={() => history('/create_operation')}>
              Nuevas Operaciones
            </OutlineButton>
          </div>
          <form onSubmit={handleSubmit(submitAction)} className="bg-white shadow sm:rounded-lg">
            <div className="divide-y divide-gray-200">
              <div className="space-y-4 px-8 pt-4 pb-8">
                <p className="text-2xl">Identificación del consumidor</p>
                {buildBaseInput(
                  'Tipo de Documento de Identidad',
                  'select',
                  'document_type',
                  {},
                  {
                    disabled: true,
                    options: (
                      <>
                        <option value="DNI">DNI</option>
                        <option value="CE">Carné de extranjería</option>
                        <option value="PSP">Pasaporte</option>
                        <option value="DM">Doc. Militar</option>
                      </>
                    ),
                  }
                )}
                {buildBaseInput('Número de documento', 'text', 'number', REQUIRED_RULE, {
                  disabled: true,
                })}
                {buildBaseInput('Nombres', 'text', 'name', REQUIRED_RULE, {})}
                {buildBaseInput('Apellidos', 'text', 'lname', REQUIRED_RULE, {})}
                {buildBaseInput('Teléfono', 'text', 'phone', REQUIRED_RULE, {})}
                {buildBaseInput('Correo electrónico', 'email', 'email', REQUIRED_RULE, {})}
                {buildBaseInput('Dirección', 'text', 'address', REQUIRED_RULE, {})}
              </div>
              <div className="space-y-4 px-8 pt-4 pb-8">
                <p className="text-2xl">Identificación del bien contratado</p>
                {buildBaseInput(
                  'Fecha de la operación',
                  'date',
                  'operation_date',
                  REQUIRED_RULE,
                  {}
                )}
                {buildBaseInput(
                  'Número de operación',
                  'text',
                  'operation_number',
                  REQUIRED_RULE,
                  {}
                )}
                {buildBaseInput(
                  'Descripción',
                  'textarea',
                  'operation_description',
                  REQUIRED_RULE,
                  {}
                )}
              </div>
              <div className="space-y-4 px-8 pt-4 pb-8">
                <p className="text-2xl">Detalle del reclamo</p>
                {buildBaseInput(
                  'Tipo de reclamo',
                  'select',
                  'claim_type',
                  {},
                  {
                    options: (
                      <>
                        <option value="R">Reclamo</option>
                        <option value="Q">Queja</option>
                      </>
                    ),
                  }
                )}
                {buildBaseInput('Descripción', 'textarea', 'claim_description', REQUIRED_RULE, {})}
                {buildBaseInput('Pedido', 'textarea', 'demand', REQUIRED_RULE, {})}
              </div>
            </div>
            <div className="flex justify-center bg-gray-50 py-4 px-8 sm:rounded-b-lg">
              <SolidButton color={BUTTON_COLORS.BLUE} className="w-auto px-12" isLoading={loading}>
                Enviar
              </SolidButton>
            </div>
          </form>
        </div>
      </div>
    </PrivateRoute>
  );
}
